import { useEffect, useState } from "react";
import { Icon } from "../../components/Icon.js";
import { Modal } from "../../components/Modal.js";
import { fmt, dollarsToCents } from "../../lib/money.js";
import { listBills, createBill, deleteBill, type Bill } from "../../api/bills.js";

interface Props {
  kidId: string;
  kidName: string;
  onClose: () => void;
}

function isValidDollarAmount(s: string): boolean {
  if (!s) return false;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return false;
  return Number(s) > 0;
}

export default function BillsModal({ kidId, kidName, onClose }: Props) {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New bill form state
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState("1");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listBills(kidId)
      .then((b) => {
        if (!cancelled) setBills(b);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load bills");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kidId]);

  const activeBills = bills.filter((b) => b.monthsRemaining > 0);
  const amountValid = isValidDollarAmount(amount);
  const monthsNum = parseInt(months, 10);
  const monthsValid = !isNaN(monthsNum) && monthsNum >= 1 && monthsNum <= 120;
  const canCreate = description.trim().length > 0 && amountValid && monthsValid && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    setFormError(null);
    try {
      const bill = await createBill(kidId, {
        description: description.trim(),
        amountCents: dollarsToCents(amount),
        monthsTotal: monthsNum,
      });
      setBills((prev) => [...prev, bill]);
      setDescription("");
      setAmount("");
      setMonths("1");
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to create bill");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (bill: Bill) => {
    try {
      await deleteBill(kidId, bill.rowKey);
      setBills((prev) => prev.filter((b) => b.rowKey !== bill.rowKey));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to cancel bill");
    }
  };

  return (
    <Modal title={`${kidName}'s monthly bills`} onClose={onClose}>
      <div className="stack" style={{ gap: 20 }}>
        {/* Active bills list */}
        {loading && (
          <div className="soft" style={{ fontSize: 13 }}>
            Loading…
          </div>
        )}
        {error && <div style={{ fontSize: 13, color: "var(--neg)" }}>{error}</div>}
        {!loading && activeBills.length === 0 && (
          <div className="soft" style={{ fontSize: 13 }}>
            No active bills. Add one below.
          </div>
        )}
        {activeBills.map((bill) => (
          <div
            key={bill.rowKey}
            className="row-between"
            style={{
              padding: "10px 14px",
              background: "var(--canvas-warm)",
              borderRadius: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{bill.description}</div>
              <div className="soft" style={{ fontSize: 12 }}>
                {fmt(bill.amountCents)}/mo · {bill.monthsRemaining} of {bill.monthsTotal} month
                {bill.monthsTotal !== 1 ? "s" : ""} remaining
              </div>
            </div>
            <button
              className="btn btn-ghost"
              style={{ padding: 6, color: "var(--neg)" }}
              onClick={() => void handleDelete(bill)}
              aria-label="Cancel bill"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}

        {/* Add new bill form */}
        <div
          style={{
            borderTop: "1px solid var(--hairline)",
            paddingTop: 16,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Add a monthly bill</div>
          <div className="stack" style={{ gap: 12 }}>
            <div className="input-group">
              <label className="label">Description</label>
              <input
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Prodigy subscription"
                maxLength={80}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="input-group">
                <label className="label">Amount per month</label>
                <div className="row">
                  <div style={{ fontSize: 22, fontFamily: "Fraunces, serif" }}>$</div>
                  <input
                    className="input"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="5.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div className="input-group">
                <label className="label">Number of months</label>
                <input
                  className="input"
                  type="number"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                  min="1"
                  max="120"
                />
              </div>
            </div>
            {formError && (
              <div style={{ fontSize: 12, color: "var(--neg)" }} role="alert">
                {formError}
              </div>
            )}
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-brand"
                onClick={() => void handleCreate()}
                disabled={!canCreate}
              >
                {creating ? "Adding…" : "Add bill"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
